// KYT v2 · WALLETS — the treasury, in today's three lists redrawn on the kit (the Command Centre, 27 Oct 2026: "redraw
// Wallets and System on the kit, keep today's lists as they are" — the one-list coverage view is N-036 (4), held):
// HOLDINGS, what each treasury pot holds (the Books' own figures, as on Wallets; money on chain not booked yet is said
// beside them, never added in); DESK WALLETS, this month's — each wallet's own GL picture (what an exchange sees behind a
// deposit from it) and what it received, by level, with its reader's state; WALLETS IN THE BOOKS, the register,
// a big pot folded to a line and the retired addresses under their own head. Every figure, word and rule is Kyt.tsx's,
// imported. No green: a band, a watch state, a clean count are information (quiet()). A row opens the lane's own wallet
// trace (?wallet=<chain>:<address>, so Back closes it and a link can land on it).
import { useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import type { KytBooksWallet, KytIncoming, KytIncomingWallet, KytOverview, KytWatchedWallet, TreasuryOutside } from '../../../lib/api';
import { errorWords } from '../../../lib/errors';
import { displayAsset, fmtAmount, fmtDay, fmtRelative, fmtStamp } from '../../../lib/format';
import { partyName, sameAsset } from '../../../lib/naming';
import type { ReadState } from '../../../lib/read-state';
import {
  GL_BAND_TONE, RETIRED_SHOWN, TRACE_UNREAD, WalletTrace, count, dollars, emptyPot, firstSentence, heldOf, holdingsOf, looked,
  networkWord, outsideOf, paymentsUnread, potsOf, readFailure, readerTrouble, registerLayout, sameWallet, traceInfo, whole,
  windowCounts, windowLabel, type Pot, type PotHolding, type TreasuryRead,
} from '../../../screens/Kyt';
import { Pill } from '../../Pill';
import { Address, EmptyState, Panel, Register, SkeletonRows, StateLine, type RegisterColumn } from '../kit';
import { shortAddress } from '../../kit';
import { BandTag, CardLine, compactWallet, quiet, useTier, type Tier } from './parts';

const GAP = 'mb-4 lg:mb-5';
const keyOf = (w: { chain: string; address: string }): string => `${w.chain}:${w.address}`;
const nameOf = (w: KytBooksWallet): string => partyName({ display_name: w.display_name, slug: w.slug });
const Chevron = () => <ChevronRight className="h-4 w-4 shrink-0 text-tg-hint" aria-hidden />;
const Retry = ({ onClick }: { onClick: () => void }) => <button type="button" className="btn-link text-body" onClick={onClick}>Try again</button>;

function parseKey(v: string | null): { chain: string; address: string } | null {
  if (!v) return null;
  const i = v.indexOf(':');
  return i > 0 ? { chain: v.slice(0, i), address: v.slice(i + 1) } : null;
}

export function WalletsV2({ d, ovState, inc, incState, incError, t, budgetOut, pick = () => true, onRetryOv, onRetryInc }: {
  d: KytOverview | undefined;
  ovState: ReadState;
  inc: KytIncoming | undefined;
  incState: ReadState;
  incError: unknown;
  t: TreasuryRead;
  budgetOut: boolean;
  /** The wallet filter (the shell's `?into=`): the desk list and its figures are of these wallets alone. */
  pick?: (w: KytIncomingWallet) => boolean;
  onRetryOv: () => void;
  onRetryInc: () => void;
}) {
  const [search, setSearch] = useSearchParams();
  const navigate = useNavigate();
  const openedHere = useRef(false);
  const books = d?.books_wallets ?? null;
  // The treasury alone: the KYT page is the treasury's (the operator, 7 Oct 2026) — no client's or merchant's wallet is here.
  const rows = (books ?? []).filter((w) => w.party_type === 'own_account');
  // A watched wallet that is not one of this month's desk wallets and that the Books do not know at all: nobody registered
  // it. With the Books unread nothing can be told apart, so every one stays.
  const others = inc && d ? d.wallets.filter((w) => !inc.wallets.some((x) => sameWallet(x, w))) : [];
  const strays = books ? others.filter((w) => !books.some((b) => sameWallet(b, w))) : others;
  const open = parseKey(search.get('wallet'));
  const openWallet = (w: { chain: string; address: string }) => {
    openedHere.current = true;
    const next = new URLSearchParams(search);
    next.set('wallet', keyOf(w));
    setSearch(next);
  };
  const closeWallet = () => {
    if (openedHere.current) { openedHere.current = false; navigate(-1); return; }
    const next = new URLSearchParams(search);
    next.delete('wallet');
    setSearch(next, { replace: true });
  };
  return (
    <>
      {d
        ? <HoldingsPanel pots={potsOf(rows)} t={t} registerRead={books !== null} />
        : ovState === 'failed'
          ? <NotRead title="Holdings" onRetry={onRetryOv} />
          : <Panel title="Holdings" flush className={GAP}><SkeletonRows rows={2} what="the treasury" /></Panel>}
      <DeskPanel inc={inc} d={d} incState={incState} incError={incError} strays={strays} booksRead={books !== null} pick={pick} onOpen={openWallet} onRetry={onRetryInc} />
      {d
        ? <TreasuryPanel d={d} rows={rows} books={books} onOpen={openWallet} />
        : ovState === 'failed'
          ? <NotRead title="Wallets in the Books" onRetry={onRetryOv} />
          : <Panel title="Wallets in the Books" flush className={GAP}><SkeletonRows rows={3} what="the register" /></Panel>}
      {open && (
        <WalletTrace chain={open.chain} address={open.address}
          desk={inc?.wallets.find((w) => sameWallet(w, open))} watched={d?.wallets.find((w) => sameWallet(w, open))} books={d?.books_wallets?.find((w) => sameWallet(w, open))}
          trace={d ? traceInfo(d, budgetOut) : TRACE_UNREAD} onClose={closeWallet} />
      )}
    </>
  );
}

/** A section whose read failed: said in red, with the way out — never left out as though there were nothing to show. */
function NotRead({ title, onRetry }: { title: string; onRetry: () => void }) {
  return <Panel title={title} className={GAP}><StateLine tone="bad" action={<Retry onClick={onRetry} />}>Not read.</StateLine></Panel>;
}

// ── Holdings: what each treasury pot holds ───────────────────────────────────

/** One row an ASSET a pot holds (1 Nov 2026, his "one fact per cell": two holdings in one cell were two facts): the pot, its
 *  networks, the Books' balance of that asset, what waits on chain unbooked, and the rate book's value of that asset alone. */
type HoldingRow = { key: string; p: Pot; asset: string; networks: string[]; balance: number; outside: TreasuryOutside | null; value: number | null; priced: boolean };
function assetRows(rows: Array<{ p: Pot; h: PotHolding | null }>, priced: boolean): HoldingRow[] {
  const out: HoldingRow[] = [];
  for (const { p, h } of rows) {
    if (!h) continue;
    const held = heldOf(h);
    const assets = [...held.map((a) => a.asset), ...outsideOf(h).filter((a) => !held.some((x) => sameAsset(x.asset, a.asset))).map((a) => a.asset)];
    for (const asset of assets) {
      const a = h.assets.find((x) => sameAsset(x.asset, asset));
      if (!a) continue;
      // The rail may spell one coin two ways (USDT, USDT-TRC20): its value is the sum of every entry of that coin, and
      // unknown when any of them is unpriced — never the first entry's figure (Astra, 1 Nov 2026).
      const priced_ = priced ? (h.value?.assets ?? []).filter((x) => sameAsset(x.asset, asset)) : [];
      const value = priced_.length === 0 || priced_.some((x) => x.value_usdt == null) ? null : priced_.reduce((n, x) => n + (x.value_usdt ?? 0), 0);
      // The asset's own networks (screens/Kyt.tsx holdingNetwork); the pot's addresses' networks only when the asset says none.
      const networks = a.networks.length ? a.networks : p.chains.map(networkWord);
      out.push({ key: `${p.slug}:${asset}`, p, asset, networks, balance: a.balance, outside: a.outside && a.outside.amount !== 0 ? a.outside : null, value, priced });
    }
  }
  return out;
}
/** A figure and its unit: the figure never splits; the unit drops under it when the cell is short of room (a 1,024 window). */
const Fig = ({ figure, unit, className = '' }: { figure: string; unit: string; className?: string }) => (
  <span className={`inline-block max-w-full ${className}`}><span className="whitespace-nowrap font-mono tabular">{figure}</span> <span className="whitespace-nowrap text-meta text-tg-hint">{unit}</span></span>
);
const Hold = ({ r }: { r: HoldingRow }) => (r.balance !== 0 ? <Fig figure={fmtAmount(r.balance, r.asset)} unit={displayAsset(r.asset)} /> : <span className="text-tg-hint">—</span>);
/** What waits on chain and is not booked, in amber — the words behind it are its title. */
const Unbooked = ({ r }: { r: HoldingRow }) => (r.outside
  ? <span title={r.outside.words}><Fig figure={`+ ${fmtAmount(r.outside.amount, r.asset)}`} unit={displayAsset(r.asset)} className="text-warn" /></span>
  : <span className="font-mono tabular text-tg-hint">—</span>);
const Value = ({ r }: { r: HoldingRow }) => (!r.priced
  ? <span className="text-tg-hint">—</span>
  : r.value != null ? <Fig figure={`≈ ${fmtAmount(r.value, 'USDT', 0)}`} unit="USDT" /> : <span className="text-tg-hint">No price</span>);
const holdingColumns = (anyOutside: boolean): ReadonlyArray<RegisterColumn<HoldingRow>> => [
  { key: 'pot', head: 'Pot', width: 'w-[26%]', cell: (r) => <span className="block break-words font-medium">{r.p.name}</span> },
  // Per ASSET, never the pot's: the Ledger's APT, BNB, ETH, GRAM and TRX read "BTC" when the pot's networks stood here (his screen, 3 Nov 2026).
  { key: 'network', head: 'Network', width: 'w-[14%]', cell: (r) => <span className="text-tg-hint">{r.networks.join(' · ')}</span> },
  { key: 'holds', head: 'Holds', align: 'right', cell: (r) => <Hold r={r} /> },
  ...(anyOutside ? [{ key: 'outside', head: 'Unbooked', align: 'right' as const, width: 'w-[18%]', cell: (r: HoldingRow) => <Unbooked r={r} /> }] : []),
  { key: 'value', head: 'Value', align: 'right', width: 'w-[22%]', cell: (r) => <Value r={r} /> },
];
const holdingCard = (r: HoldingRow) => ({
  // The asset's networks, as the table's cell (Astra on 64477e5b: the card still said the pot's).
  title: <>{r.p.name}<span className="block text-meta font-normal text-tg-hint">{r.networks.join(' · ')}</span></>,
  figure: <Hold r={r} />,
  meta: (
    <span className="block whitespace-normal">
      <CardLine label="Value"><Value r={r} /></CardLine>
      {r.outside && <CardLine label="Unbooked"><Unbooked r={r} /></CardLine>}
    </span>
  ),
});

/** The Books' own figures, per POT (a pot's addresses are ONE balance in the Books). A read that failed says so — never a
 *  row of zeros; with the rate book unread there are quantities, no values; a total only when every pot holding
 *  something is priced in full. */
function HoldingsPanel({ pots, t, registerRead }: { pots: Pot[]; t: TreasuryRead; registerRead: boolean }) {
  const { readable, priced, rows, total, statedAt, oldestStated } = holdingsOf(pots, t);
  const held = rows.filter(({ h }) => !emptyPot(h));
  const empty = rows.filter(({ h }) => emptyPot(h));
  const lines = assetRows(held, priced);
  const failed = t.isError ? firstSentence(errorWords(t.error)) : null;
  return (
    <Panel title="Holdings" flush className={GAP}>
      {failed && <StateLine tone="warn" className="px-4 py-3 lg:px-5" more={failed.rest || undefined}>Couldn&apos;t read the treasury&apos;s holdings — {failed.first}.</StateLine>}
      {!t.isError && !t.data && <SkeletonRows rows={2} what="the treasury" />}
      {t.data && !readable && <StateLine tone="warn" className="px-4 py-3 lg:px-5">Holdings unreadable here — they are on Wallets.</StateLine>}
      {readable && !priced && <p className="px-4 pt-3 text-meta text-warn lg:px-5">The rate book could not be read — quantities only, no values.</p>}
      {readable && pots.length === 0 && !registerRead && <StateLine tone="warn" className="px-4 py-3 lg:px-5">Register not read — holdings unknown.</StateLine>}
      {readable && pots.length === 0 && registerRead && <EmptyState>No wallet registered in the Books.</EmptyState>}
      {readable && lines.length > 0 && <Register label="Holdings" rows={lines} rowKey={(r) => r.key} columns={holdingColumns(lines.some((r) => !!r.outside))} card={holdingCard} />}
      {/* The total in all, a figure at body size (the audit, 1 Nov 2026: money totals in meta), its estimate words beside it. */}
      {readable && total != null && (
        <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 border-t border-stroke-soft px-4 py-3 lg:px-5">
          <span className="font-mono tabular text-body">≈ {fmtAmount(total, 'USDT', 0)} USDT <span className="font-sans text-tg-hint">in all</span></span>
          {statedAt.length > 0 && <span className="text-meta text-tg-hint">partly at stated prices{oldestStated ? ` (${fmtDay(oldestStated)})` : ''} — an estimate</span>}
        </div>
      )}
      {/* The pots holding nothing: one line, not a row each (7 Oct 2026, "minimise the clutter"). */}
      {readable && empty.length > 0 && <p className="border-t border-stroke-soft px-4 py-3 text-meta text-tg-hint lg:px-5">Nothing held: {empty.map(({ p }) => p.name).join(', ')}</p>}
    </Panel>
  );
}

// ── Desk wallets: this month's, each with its own GL picture and what it received ──

/** A row's name: the label alone — the chain is a column of its own and the address is in the opened wallet (the operator,
 *  28 Oct 2026: the chain said twice, rows from one line to seven). */
function WalletName({ w, retired = null }: { w: { label?: string | null; chain: string }; retired?: string | null }) {
  // Wraps, never cut ("Hot · TRO…" at 1024 — the audit, 1 Nov 2026); the whole label is its title. A retired address says so under
  // its name (3 Nov 2026: two "Merchant Hot · TRC20" rows, one retired 30 Oct, could not be told apart — the name is Mechanisms'
  // N-091 tag to fix; the register's retired day is a fact of its own).
  return (
    <span className="block min-w-0 break-words font-medium" title={w.label ?? undefined}>
      {w.label ? compactWallet(w.label) : `${w.chain} wallet`}
      {retired && <span className="block text-meta font-normal text-tg-hint">Retired {fmtDay(retired, true)}</span>}
    </span>
  );
}
/** The register's retired day for a desk wallet (the Books' row for the same address on the same network), else null. */
const retiredOf = (d: KytOverview | undefined) => (w: { chain: string; address: string }): string | null =>
  d?.books_wallets?.find((b) => sameWallet(b, w) && b.status === 'archived')?.retired_at ?? null;
/** GL's band for the wallet itself, the pill alone (its score is the cell beside it — two facts, two cells, 1 Nov 2026); when it
 *  was scored is its title. A low score GL gave with little data is plain, never a clean bill. The sources, the dates and the
 *  notes are the opened wallet's. */
function GlBand({ w }: { w: KytIncomingWallet }) {
  if (!w.gl) return <Pill>Not scored</Pill>;
  return (
    <span title={w.gl.at ? `scored ${fmtRelative(w.gl.at)}` : undefined}>
      <Pill tone={quiet(w.gl.thin && w.gl.band === 'low' ? 'neutral' : GL_BAND_TONE[w.gl.band])}>{w.gl.band}</Pill>
    </span>
  );
}
const GlScoreCell = ({ w }: { w: KytIncomingWallet }) => (w.gl ? <span className="font-mono tabular">{whole(Math.round(w.gl.score))}</span> : <Dash />);
/** A count in its column: tabular, toned only above zero, muted at zero. */
const Num = ({ n, tone }: { n: number; tone?: 'bad' | 'warn' }) => (
  <span className={`font-mono tabular ${n > 0 ? (tone === 'bad' ? 'text-neg' : tone === 'warn' ? 'text-warn' : '') : 'text-tg-hint'}`}>{whole(n)}</span>
);
const Dash = () => <span className="font-mono tabular text-tg-hint">—</span>;
/** "0 payments" over a wallet not read in full is not a count: its figures are dashes and the status says why. */
const unknown = (w: KytIncomingWallet): boolean => w.window.payments === 0 && paymentsUnread(w);
/** Only when something is wrong (28 Oct 2026): the network not read, a reader failing or stepping over transfers, a count
 *  not known. A wallet whose payments are read says nothing here. */
function StatusCell({ w }: { w: KytIncomingWallet }) {
  if (!w.read) return <span className="text-warn">Not read on this network</span>;
  const x = readerTrouble(w)[0];
  // A word or three; the reader's own sentence is the mark's title and the opened wallet's (28 Oct 2026: no sentence in a cell).
  if (x) return <span role={x.tone === 'bad' ? 'alert' : undefined} className={x.tone === 'bad' ? 'text-neg' : 'text-warn'} title={x.state}>{x.tone === 'bad' ? 'Reader failing' : 'Read in part'}</span>;
  if (unknown(w)) return <span className="text-warn">Not known</span>;
  return null;
}
/** One line a wallet, numbers in columns (28 Oct 2026), one fact a cell (1 Nov 2026): the wallet, its chain, GL's score, its
 *  band, then the window's payments and dollars, the alerts, the watches, the payments not fully checked — and a status
 *  mark only when something is wrong. The chain and the dollars need the room of a 1,400 px window (parts.tsx useTier). */
const deskColumns = (tier: Tier, retired: (w: KytIncomingWallet) => string | null): ReadonlyArray<RegisterColumn<KytIncomingWallet>> => {
  const full = tier === 'full';
  const cols: Array<RegisterColumn<KytIncomingWallet> | false> = [
    { key: 'wallet', head: 'Wallet', width: full ? 'w-[20%]' : 'w-[22%]', cell: (w) => <WalletName w={w} retired={retired(w)} /> },
    full && { key: 'chain', head: 'Chain', width: 'w-[64px]', cell: (w) => <span className="font-mono text-meta text-tg-hint">{w.chain}</span> },
    { key: 'score', head: 'GL score', align: 'right', width: full ? 'w-[80px]' : 'w-[72px]', cell: (w) => <GlScoreCell w={w} /> },
    { key: 'band', head: 'Band', width: full ? 'w-[96px]' : 'w-[88px]', cell: (w) => <GlBand w={w} /> },
    { key: 'payments', head: 'Payments', align: 'right', width: full ? 'w-[84px]' : 'w-[76px]', cell: (w) => (unknown(w) ? <Dash /> : <Num n={w.window.payments} />) },
    full && { key: 'volume', head: 'Volume', align: 'right', width: 'w-[136px]', cell: (w) => (unknown(w) || w.window.payments === 0 ? <Dash /> : <span className="font-mono tabular">{dollars(w.window.usd)}</span>) },
    { key: 'alerts', head: 'Alerts', align: 'right', width: full ? 'w-[68px]' : 'w-[60px]', cell: (w) => <Num n={w.window.by.alert.n} tone="bad" /> },
    { key: 'watch', head: 'Watch', align: 'right', width: full ? 'w-[68px]' : 'w-[60px]', cell: (w) => <Num n={w.window.by.watch.n} tone="warn" /> },
    { key: 'unchecked', head: 'Not checked', align: 'right', width: full ? 'w-[96px]' : 'w-[88px]', cell: (w) => <Num n={w.window.by.unchecked.n} tone="warn" /> },
    { key: 'status', head: 'Status', cell: (w) => <StatusCell w={w} /> },
  ];
  return cols.filter((c): c is RegisterColumn<KytIncomingWallet> => !!c);
};
const deskCard = (retired: (w: KytIncomingWallet) => string | null) => (w: KytIncomingWallet) => ({
  title: <WalletName w={w} retired={retired(w)} />,
  figure: <GlBand w={w} />,
  // The laptop's columns as labelled lines (the audit, 1 Nov 2026: a card loses no fact the table has).
  meta: (
    <span className="block whitespace-normal">
      {w.gl && <CardLine label="GL score"><span className="font-mono tabular">{whole(Math.round(w.gl.score))}</span></CardLine>}
      {unknown(w)
        ? <span className="block text-warn">Payments not known</span>
        : <>
          <CardLine label="Payments"><span className="font-mono tabular">{whole(w.window.payments)}</span></CardLine>
          {w.window.payments > 0 && <CardLine label="Volume"><span className="font-mono tabular">{dollars(w.window.usd)}</span></CardLine>}
        </>}
    </span>
  ),
  state: w.window.by.alert.n > 0
    ? <span className="text-meta text-neg">{count(w.window.by.alert.n, 'alert', 'alerts')}</span>
    : w.window.by.watch.n > 0 ? <span className="text-meta text-warn">{whole(w.window.by.watch.n)} watch</span> : <span className="text-meta"><StatusCell w={w} /></span>,
});

/** A wallet the screener watches as the overview has it: the fallback rows when the payments read is not there, and the
 *  watched wallets nobody registered. */
const watchedColumns = (entity: boolean): ReadonlyArray<RegisterColumn<KytWatchedWallet>> => [
  { key: 'wallet', head: 'Wallet', cell: (w) => <span className="block break-words font-medium">{w.label ?? <span className="text-tg-hint">—</span>}</span> },
  ...(entity ? [{ key: 'entity', head: 'Entity', width: 'w-[18%]', cell: (w: KytWatchedWallet) => <span className="text-tg-hint">{w.entity ?? '—'}</span> }] : []),
  { key: 'chain', head: 'Chain', width: 'w-[72px]', cell: (w) => <span className="font-mono text-meta text-tg-hint">{w.chain}</span> },
  { key: 'address', head: 'Address', width: 'w-[22%]', cell: (w) => <Address value={w.address} /> },
  { key: 'band', head: 'Band', width: 'w-[14%]', cell: (w) => <BandTag band={w.band} looked={looked(w)} /> },
  { key: 'checked', head: 'Last checked', width: 'w-[16%]', cell: (w) => <span className="text-tg-hint" title={w.lastChecked ? fmtStamp(w.lastChecked) : undefined}>{w.lastChecked ? fmtRelative(w.lastChecked) : 'never'}</span> },
];
const watchedCard = (w: KytWatchedWallet) => ({
  title: <>{w.label ?? '—'}{w.entity && <span className="block text-meta font-normal text-tg-hint">{w.entity}</span>}</>,
  figure: <BandTag band={w.band} looked={looked(w)} />,
  meta: <span className="inline-flex items-center gap-x-2"><span className="font-mono">{w.chain}</span><Address value={w.address} /></span>,
  state: <span className="text-meta text-tg-hint">{w.lastChecked ? fmtRelative(w.lastChecked) : 'never'}</span>,
});

function DeskPanel({ inc, d, incState, incError, strays, booksRead, pick, onOpen, onRetry }: {
  inc: KytIncoming | undefined; d: KytOverview | undefined; incState: ReadState; incError: unknown;
  strays: KytWatchedWallet[]; booksRead: boolean; pick: (w: KytIncomingWallet) => boolean; onOpen: (w: { chain: string; address: string }) => void; onRetry: () => void;
}) {
  const tier = useTier();
  const failed = incState === 'failed' ? readFailure(incError) : null;
  const listed = inc ? inc.wallets.filter(pick) : [];
  return (
    <Panel title="Desk wallets" aside={inc ? windowLabel(inc) : failed ? <span className="text-warn">not read</span> : undefined} flush className={GAP}>
      {failed && <StateLine tone="bad" className="px-4 py-3 lg:px-5" more={failed.more} action={<Retry onClick={onRetry} />}>Desk wallets not read — {failed.why}.</StateLine>}
      {incState === 'waiting' && !inc && <SkeletonRows rows={2} what="the desk wallets" />}
      {/* Nothing watched is not a quiet day: nothing is being read — said in amber. */}
      {inc && inc.wallets.length === 0 && (
        <StateLine tone="warn" className="px-4 py-3 lg:px-5" more={<>In the ops group: <span className="font-mono">/watch TRC20 T… treasury</span></>}>
          No wallet watched — add one with <span className="font-mono">/watch</span>.
        </StateLine>
      )}
      {inc && listed.length > 0 && <Register label="Desk wallets" rows={listed} rowKey={keyOf} onOpen={onOpen} columns={deskColumns(tier, retiredOf(d))} card={deskCard(retiredOf(d))} />}
      {inc && listed.length > 0 && (() => {
        const c = windowCounts(inc, pick);
        return (
          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 border-t border-stroke-soft px-4 py-3 text-meta text-tg-hint lg:px-5">
            <span className="font-mono tabular text-body text-tg-text">
              {count(listed.length, 'wallet', 'wallets')} · {count(c.payments, 'payment', 'payments')} · {dollars(c.usd)}
              {c.alerts.n > 0 && ` · ${count(c.alerts.n, 'alert', 'alerts')}`}
              {c.watch.n > 0 && ` · ${whole(c.watch.n)} watch`}
              {c.unread > 0 && ` · ${count(c.unread, 'wallet', 'wallets')} not read`}
            </span>
            <span>{windowLabel(inc)}</span>
          </div>
        );
      })()}
      {inc && strays.length > 0 && (
        <>
          <p className="border-t border-stroke-soft px-4 pt-3 text-label font-medium uppercase tracking-wider text-tg-hint lg:px-5">
            {booksRead ? 'Watched, not in the Books · nobody registered them' : 'Also watched · payments into them not read'}
          </p>
          <Register label="Watched wallets" rows={strays} rowKey={keyOf} onOpen={onOpen} columns={watchedColumns(strays.some((w) => !!w.entity))} card={watchedCard} />
        </>
      )}
      {/* Without the payments read, the watchlist still stands — as the overview has it. */}
      {!inc && d && (d.wallets.length === 0
        ? <StateLine tone="warn" className="px-4 py-3 lg:px-5">No wallet watched — add one with <span className="font-mono">/watch</span>.</StateLine>
        : <Register label="Watched wallets" rows={d.wallets} rowKey={keyOf} onOpen={onOpen} columns={watchedColumns(d.wallets.some((w) => !!w.entity))} card={watchedCard} />)}
    </Panel>
  );
}

// ── The wallets the Books register ──────────────────────────────────

/** One fact a cell (1 Nov 2026): the party, the wallet's own name when it has one, the day it was retired (the retired list
 *  only — it used to be glued to the party), the chain, the address, the watch state, the recorded band and its score. The
 *  chain and the score need the room of a 1,400 px window (parts.tsx useTier). */
function booksColumns({ labels, watchCol, riskCol, retired, tier }: { labels: boolean; watchCol: boolean; riskCol: boolean; retired: boolean; tier: Tier }): ReadonlyArray<RegisterColumn<KytBooksWallet>> {
  const full = tier === 'full';
  const cols: Array<RegisterColumn<KytBooksWallet> | false> = [
    { key: 'party', head: 'Party', width: labels ? 'w-[18%]' : undefined, cell: (w) => <span className="block break-words font-medium">{nameOf(w)}</span> },
    labels && { key: 'label', head: 'Wallet', cell: (w) => <span className="block break-words">{w.label && w.label !== nameOf(w) ? w.label : <span className="text-tg-hint">—</span>}</span> },
    // A retired address stays on this list, and in the count: it can still be paid into and the Books still read it.
    retired && { key: 'retired', head: 'Retired', width: 'w-[112px]', cell: (w) => <span className="whitespace-nowrap text-tg-hint">{w.retired_at ? fmtDay(w.retired_at, true) : '—'}</span> },
    full && { key: 'chain', head: 'Chain', width: 'w-[72px]', cell: (w) => <span className="font-mono text-meta">{w.chain}</span> },
    { key: 'address', head: 'Address', width: full ? 'w-[180px]' : 'w-[140px]', cell: (w) => <Address value={w.address} /> },
    // Watched and the recorded risk stand only when they tell one row from another (7 Oct 2026, "minimise the clutter").
    watchCol && { key: 'watched', head: 'Watched', width: 'w-[120px]', cell: (w) => <WatchState w={w} /> },
    riskCol && { key: 'band', head: 'Band', width: 'w-[96px]', cell: (w) => <BandTag band={w.band} looked={looked(w)} /> },
    riskCol && full && { key: 'score', head: 'Score', align: 'right', width: 'w-[72px]', cell: (w) => (w.risk_score != null ? <span className="font-mono tabular">{w.risk_score}</span> : <Dash />) },
    { key: 'open', head: '', width: 'w-[48px]', align: 'right', cell: () => <Chevron /> },
  ];
  return cols.filter((c): c is RegisterColumn<KytBooksWallet> => !!c);
}
/** The watch state in a word or three (3 Nov 2026, his Hot · Base "not watched" beside the same key's Ethereum "watched"):
 *  watched — in the worker's desk list (payments read, GL scored); freeze watch only — the freeze watch holds it on an
 *  issuer's network, but the payment reader has no reader for that network and GL no score (the words behind it are its
 *  title); not watched — nobody screens it. */
function WatchState({ w }: { w: KytBooksWallet }) {
  if (w.watched) return <span className="text-tg-hint">watched</span>;
  if (w.freeze_watched) {
    const net = networkWord(w.chain);
    return <span className="text-warn" title={`Frozen coins watched${w.swept_at ? `, last ${fmtRelative(w.swept_at)}` : ''}; payments on ${net} are not read here and GL gives no ${net} score`}>freeze watch only</span>;
  }
  return <span className="text-warn" title={`/watch ${w.chain} ${w.address}${w.label ? ` ${w.label}` : ''}`}>not watched</span>;
}
const booksCard = (w: KytBooksWallet) => {
  const n = nameOf(w);
  return {
    title: <>{n}{w.label && w.label !== n && <span className="block text-meta font-normal text-tg-hint">{w.label}</span>}{w.status === 'archived' && <span className="block text-meta font-normal text-tg-hint">Retired{w.retired_at ? ` ${fmtDay(w.retired_at, true)}` : ''}</span>}</>,
    figure: <Chevron />,
    meta: <span className="inline-flex items-center gap-x-2"><span className="font-mono">{w.chain}</span><Address value={w.address} /></span>,
    state: <span className="inline-flex items-center gap-1.5">{!w.watched && <span className="text-meta"><WatchState w={w} /></span>}{(w.band || w.risk_score != null) && <BandTag band={w.band} looked={looked(w)} />}</span>,
  };
};

/** The register: this month's wallets (a pot with many addresses folded to one line until opened), then the retired
 *  addresses — the unwatched first, a few at a time: the exceptions are on the page, the rest a press away (6 Oct 2026). */
function TreasuryPanel({ d, rows, books, onOpen }: { d: KytOverview; rows: KytBooksWallet[]; books: KytBooksWallet[] | null; onOpen: (w: { chain: string; address: string }) => void }) {
  const [all, setAll] = useState(false);
  const [opened, setOpened] = useState<string[]>([]);
  const tier = useTier();
  const { active, folded, retired, shown, watchedRetired } = registerLayout(rows, opened, all);
  const labels = rows.some((w) => !!w.label && w.label !== nameOf(w));
  const watchCol = rows.some((w) => !w.watched);
  const riskCol = rows.some((w) => !!w.band || w.risk_score != null);
  const columns = booksColumns({ labels, watchCol, riskCol, retired: false, tier });
  const retiredColumns = booksColumns({ labels, watchCol, riskCol, retired: true, tier });
  const legend = [
    rows.some((w) => !w.watched && !w.freeze_watched) && 'not watched = nobody screens it',
    rows.some((w) => !w.watched && w.freeze_watched) && 'freeze watch only = frozen coins watched, payments not read',
    !!d.coverage?.retired && 'retired = still paid into',
  ].filter(Boolean).join(' · ');
  const aside = (
    <span className="inline-flex flex-wrap gap-x-3">
      {d.coverage && <span className={d.coverage.watched < d.coverage.treasury ? 'text-warn' : ''}>{whole(d.coverage.watched)} of {whole(d.coverage.treasury)} watched</span>}
      {legend && <span>{legend}</span>}
    </span>
  );
  return (
    <Panel title="Wallets in the Books" aside={aside} flush className={GAP}>
      {/* The register's own failure is the Books', and its text a database's: the line says what to do, never the raw error. */}
      {d.books_error && (
        <StateLine tone="warn" className="px-4 py-3 lg:px-5" more="The register is read from the Books' database, and it did not answer. The wallets and their watch state are unknown until it does. If Refresh does not help, check that Docker is running on the desk Mac.">
          Register not read — the Books did not answer.
        </StateLine>
      )}
      {books && rows.length === 0 && <EmptyState>No wallet registered in the Books.</EmptyState>}
      {active.length > 0 && <Register label="Wallets in the Books" rows={active} rowKey={keyOf} onOpen={onOpen} columns={columns} card={booksCard} />}
      {folded.map((f) => (
        <div key={`pot:${f.key}`} className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 border-t border-stroke-soft px-4 py-3 text-body lg:px-5">
          <span className="min-w-0">
            <span className="break-words font-medium">{f.name}</span>
            <span className="text-meta text-tg-hint"> · {f.chains.join(' · ')} · {whole(f.n)} addresses · <span className={f.watched < f.n ? 'text-warn' : ''}>{f.watched === 0 ? 'none watched' : `${whole(f.watched)} of ${whole(f.n)} watched`}</span></span>
          </span>
          <button type="button" className="btn-link text-body" onClick={() => setOpened((o) => [...o, f.key])}>Show them</button>
        </div>
      ))}
      {retired.length > 0 && (
        <>
          <div className="flex flex-wrap items-baseline gap-x-3 border-t border-stroke-soft px-4 pb-1 pt-3 lg:px-5">
            <span className="text-label font-medium uppercase tracking-wider text-tg-hint">Retired addresses · {whole(retired.length)}</span>
            <span className={`text-meta ${watchedRetired < retired.length ? 'text-warn' : 'text-tg-hint'}`}>{whole(watchedRetired)} of {whole(retired.length)} watched</span>
          </div>
          <Register label="Retired addresses" rows={shown} rowKey={keyOf} onOpen={onOpen} columns={retiredColumns} card={booksCard} />
          {retired.length > RETIRED_SHOWN && (
            <div className="border-t border-stroke-soft px-4 py-3 lg:px-5">
              <button type="button" className="btn-link text-body" onClick={() => setAll(!all)}>{all ? 'Show fewer' : `Show all ${whole(retired.length)}`}</button>
            </div>
          )}
        </>
      )}
    </Panel>
  );
}
